<p-toast></p-toast>

<app-page-shell>
  <app-page-hero
    eyebrow="Warehousing Setup"
    title="Hub Lanes"
    subtitle="Approved warehouse-to-warehouse lanes used by the cross-dock route engine."
    backLink="/warehousing/setup/storage-rates">
    <div actions>
      <p-button label="Warehouses" icon="pi pi-warehouse" severity="secondary" [outlined]="true" routerLink="/setup/warehouses"></p-button>
      <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" [outlined]="true" (onClick)="load()"></p-button>
    </div>
  </app-page-hero>

  <app-page-data-panel
    [title]="editingId ? 'Edit Hub Lane' : 'New Hub Lane'"
    subtitle="Use explicit lanes for multi-hop routing instead of inferring all moves from geography."
    [resultText]="rows.length + ' lanes'">
    <form [formGroup]="form" class="lane-form" (ngSubmit)="save()">
      <div class="lane-field">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-warehouse"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <p-select inputId="sourceWarehouseId" formControlName="sourceWarehouseId" [options]="warehouses" optionLabel="name" optionValue="id" [filter]="true" filterBy="name,code" appendTo="body" styleClass="w-full"></p-select>
            <label for="sourceWarehouseId">Source Warehouse</label>
          </p-iftaLabel>
        </p-inputGroup>
        @if (form.get('sourceWarehouseId')?.invalid && form.get('sourceWarehouseId')?.touched) { <small class="p-error">Source warehouse is required.</small> }
      </div>
      <div class="lane-field">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <p-select inputId="destinationWarehouseId" formControlName="destinationWarehouseId" [options]="warehouses" optionLabel="name" optionValue="id" [filter]="true" filterBy="name,code" appendTo="body" styleClass="w-full"></p-select>
            <label for="destinationWarehouseId">Destination Warehouse</label>
          </p-iftaLabel>
        </p-inputGroup>
        @if (form.get('destinationWarehouseId')?.invalid && form.get('destinationWarehouseId')?.touched) { <small class="p-error">Destination warehouse is required.</small> }
      </div>
      <div class="lane-field">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-sitemap"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <p-select inputId="laneType" formControlName="laneType" [options]="laneTypeOptions" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
            <label for="laneType">Lane Type</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="lane-field">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-sort-numeric-up"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <p-inputNumber inputId="priority" formControlName="priority" [min]="1" [showButtons]="true" styleClass="w-full"></p-inputNumber>
            <label for="priority">Priority</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="lane-field">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-chart-line"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <p-inputNumber inputId="costRank" formControlName="costRank" [min]="1" [showButtons]="true" styleClass="w-full"></p-inputNumber>
            <label for="costRank">Cost Rank</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="lane-field">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-compass"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <p-inputNumber inputId="distanceKm" formControlName="distanceKm" [min]="0" [minFractionDigits]="0" [maxFractionDigits]="2" styleClass="w-full"></p-inputNumber>
            <label for="distanceKm">Distance KM</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="lane-field">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-clock"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <p-inputNumber inputId="transitDurationMinutes" formControlName="transitDurationMinutes" [min]="1" suffix=" min" styleClass="w-full"></p-inputNumber>
            <label for="transitDurationMinutes">Transit Duration</label>
          </p-iftaLabel>
        </p-inputGroup>
        @if (form.get('transitDurationMinutes')?.invalid && form.get('transitDurationMinutes')?.touched) { <small class="p-error">Transit duration is required.</small> }
      </div>
      <div class="lane-field">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-stopwatch"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <p-inputNumber inputId="crossDockDwellMinutes" formControlName="crossDockDwellMinutes" [min]="0" suffix=" min" styleClass="w-full"></p-inputNumber>
            <label for="crossDockDwellMinutes">Cross-Dock Dwell</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="lane-field">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-upload"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <p-inputNumber inputId="loadingMinutes" formControlName="loadingMinutes" [min]="0" suffix=" min" styleClass="w-full"></p-inputNumber>
            <label for="loadingMinutes">Loading Time</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="lane-field">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-download"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <p-inputNumber inputId="unloadingMinutes" formControlName="unloadingMinutes" [min]="0" suffix=" min" styleClass="w-full"></p-inputNumber>
            <label for="unloadingMinutes">Unloading Time</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="lane-field">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-shield"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <p-inputNumber inputId="cutoffBufferMinutes" formControlName="cutoffBufferMinutes" [min]="0" suffix=" min" styleClass="w-full"></p-inputNumber>
            <label for="cutoffBufferMinutes">Cutoff Buffer</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="lane-actions">
        <p-button label="Reset" icon="pi pi-times" severity="secondary" [outlined]="true" type="button" (onClick)="resetForm()"></p-button>
        <p-button [label]="editingId ? 'Update Lane' : 'Save Lane'" icon="pi pi-save" type="submit" [loading]="isSaving" [disabled]="isSaving"></p-button>
      </div>
    </form>
  </app-page-data-panel>

  <app-page-data-panel title="Configured Lanes" subtitle="Only active lanes are used when resolving cross-dock paths." [resultText]="rows.length + ' lanes'">
    <p-table [value]="rows" [loading]="isLoading" responsiveLayout="scroll" styleClass="workspace-table">
      <ng-template pTemplate="header">
        <tr>
          <th>Source</th>
          <th>Destination</th>
          <th>Type</th>
          <th>Priority</th>
          <th>Cost</th>
          <th>Distance</th>
          <th>Transit</th>
          <th>Dwell</th>
          <th>Status</th>
          <th>Audit</th>
          <th></th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-row>
        <tr>
          <td>{{ row.sourceWarehouseName || warehouseName(row.sourceWarehouseId) }}</td>
          <td>{{ row.destinationWarehouseName || warehouseName(row.destinationWarehouseId) }}</td>
          <td>{{ laneTypeLabel(row.laneType) }}</td>
          <td>{{ row.priority || '-' }}</td>
          <td>{{ row.costRank || '-' }}</td>
          <td>{{ row.distanceKm ?? '-' }}</td>
          <td>{{ row.transitDurationMinutes ? (row.transitDurationMinutes + ' min') : '-' }}</td>
          <td>{{ row.crossDockDwellMinutes != null ? (row.crossDockDwellMinutes + ' min') : '-' }}</td>
          <td><p-tag [value]="row.isActive === false ? 'INACTIVE' : 'ACTIVE'" [severity]="statusSeverity(row.isActive)"></p-tag></td>
          <td><app-audit-directory-cell [stamp]="row"></app-audit-directory-cell></td>
          <td class="text-right">
            <p-button icon="pi pi-pencil" [text]="true" severity="info" (onClick)="edit(row)"></p-button>
            <p-button icon="pi pi-ban" [text]="true" severity="danger" [loading]="isSaving" [disabled]="row.isActive === false || isSaving" (onClick)="deactivate(row)"></p-button>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr><td colspan="11" class="empty-cell">No hub lanes configured yet.</td></tr>
      </ng-template>
    </p-table>
  </app-page-data-panel>
</app-page-shell>
